import { Listbox } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";

import { Small } from "@fronterahealth/frontera-ui-components";

interface TableFilterDropdownItem<T extends string> {
  id: T;
  label: string;
  count?: number;
}

interface TableFilterDropdownProps<T extends string> {
  label: string;
  items: TableFilterDropdownItem<T>[];
  value: T[];
  onChange: (next: T[]) => void;
  headerSlot?: React.ReactNode;
}

// Multi-select filter dropdown for table headers. Anchors the popover to the
// trigger's bottom-right edge so it always opens below and inside the table
// card horizontally — sidesteps the popper flip behavior in Mosaic's
// SearchableDropdown which surfaces the popover beside the trigger when bottom
// space is constrained.
export function TableFilterDropdown<T extends string>({
  label,
  items,
  value,
  onChange,
  headerSlot,
}: TableFilterDropdownProps<T>) {
  return (
    <Listbox value={value} onChange={onChange} multiple>
      <div className="relative inline-block">
        <Listbox.Button className="relative flex h-10 cursor-pointer items-center gap-2 rounded-md bg-white px-3 py-1.5 text-left text-sm text-text-primary shadow-xs ring-1 ring-inset ring-border-strong focus:outline-none ui-open:ring-2 ui-open:ring-interactive-primary-default">
          <Small colorType="primary">{label}</Small>
          {value.length > 0 && (
            <Small
              data-testid="table-filter-dropdown-count"
              className="flex h-5 w-5 items-center justify-center rounded-full bg-icon-highlight p-1 text-sm text-white"
            >
              {value.length}
            </Small>
          )}
          <ChevronUpDownIcon className="h-5 w-5 text-limestone-400" aria-hidden="true" />
        </Listbox.Button>
        <Listbox.Options className="absolute right-0 z-[60] mt-2 min-w-[220px] overflow-hidden rounded-md border border-gray-300 bg-white shadow-lg focus:outline-none">
          <div className="flex items-center gap-1 border-b border-limestone-100 px-5 pb-3 pt-3 text-limestone-900">
            <span className="text-sm">Filter by {label}</span>
            {headerSlot}
          </div>
          <div className="max-h-60 overflow-auto px-2 py-2">
            {items.map((item) => (
              <Listbox.Option
                key={item.id}
                value={item.id}
                className={({ active }) =>
                  clsx(
                    "relative cursor-pointer select-none rounded-md py-2 pl-3 pr-3 text-text-primary",
                    active && "bg-bg-hovered",
                  )
                }
              >
                {({ selected }) => (
                  <div className="flex items-center gap-2">
                    <span
                      className={clsx(
                        "relative flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border",
                        selected
                          ? "border-2 border-interactive-primary-default bg-interactive-primary-default"
                          : "border-interactive-primary-default bg-white",
                      )}
                    >
                      {selected ? <CheckIcon className="h-3 w-3 stroke-[4px] text-white" aria-hidden="true" /> : null}
                    </span>
                    <span className="truncate text-sm">{item.label}</span>
                    {item.count !== undefined && (
                      <span
                        data-testid={`table-filter-dropdown-item-count-${item.id}`}
                        className="ml-auto pl-2 text-sm text-limestone-500"
                      >
                        {item.count}
                      </span>
                    )}
                  </div>
                )}
              </Listbox.Option>
            ))}
          </div>
          <button
            type="button"
            onClick={() => onChange([])}
            className="block w-full border-t border-limestone-100 px-5 py-3 text-left text-sm text-green-700 hover:bg-bg-hovered"
          >
            Clear
          </button>
        </Listbox.Options>
      </div>
    </Listbox>
  );
}
